import React from 'react';

import { Money, formatMoney, isValidMoney } from '@betterment/core';
import { Box, FormikField, Input, InputProps, Text, Tooltip } from '@betterment/samba-react';

interface MoneyInputProps extends Omit<InputProps, 'value'> {
  name: string;
  label: string;
  description?: string;
  tooltip?: string;
  isRequired?: boolean;
  showError?: boolean;
}

const MoneyInput: React.FC<Omit<InputProps, 'value'> & { value?: Money | string | null }> = ({ value, ...props }) => {
  const displayValue = isValidMoney(value) ? formatMoney(value, 'raw') : (value ?? '');

  return <Input leadingSymbol="$" mask="number" {...props} value={displayValue} />;
};

export const FormikMoneyInput: React.FC<MoneyInputProps> = ({
  name,
  label,
  description,
  tooltip,
  isRequired = false,
  showError = true,
  ...rest
}) => {
  return (
    <FormikField name={name}>
      <FormikField.Label>
        <Box css={{ display: 'flex' }}>
          {label}
          {isRequired && ' *'}
          {tooltip && (
            <Tooltip>
              <Tooltip.Trigger asChild={true}>
                <Tooltip.IconButton name="InfoCircle" css={{ ml: 'space.50' }} />
              </Tooltip.Trigger>
              <Tooltip.Content align="center" side="top">
                {tooltip}
              </Tooltip.Content>
            </Tooltip>
          )}
        </Box>
      </FormikField.Label>
      <FormikField.Control>
        <MoneyInput {...rest} aria-label={`${label}${isRequired ? ' *' : ''}`} aria-required={isRequired} />
      </FormikField.Control>
      {description && (
        <FormikField.Description>
          <Text variant="text50" color="text.weak">
            {description}
          </Text>
        </FormikField.Description>
      )}
      {showError && <FormikField.ErrorMessage />}
    </FormikField>
  );
};
